import React, { useEffect, useRef } from "react";
import { UserCog } from "lucide-react";

/**
 * Blocking notice shown when another browser tab signed in as a different user or into a
 * different organisation.
 *
 * This tab is already frozen by authService (it hands out no credentials), so nothing on
 * the page can act under either identity. The only way forward is an explicit reload, which
 * re-initialises the tab cleanly under the session that is now active in this browser. The
 * tab never switches identity silently.
 */
export default function SessionChangedNotice({ change }) {
  const buttonRef = useRef(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  const sameUser = Boolean(change?.from && change?.to && change.from.uid === change.to.uid);
  const description = sameUser
    ? "You switched to a different organisation in another tab. This tab was still showing the previous organisation, so it has been paused to keep the two organisations' data separate."
    : "A different account signed in to SynexOS in another tab. This tab was still showing the previous account, so it has been paused to keep the two accounts separate.";

  const reload = () => {
    // Land on the Workspace entry point rather than re-opening a resource that may belong to
    // the previous account or organisation. Route guards redirect members without Workspace.
    window.location.assign("/workspace");
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-changed-title"
      aria-describedby="session-changed-description"
      data-testid="session-changed-notice"
    >
      <div className="bg-white border border-slate-200/90 rounded-3xl shadow-2xl w-full max-w-md flex flex-col p-6 sm:p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200/80 text-[#033F99] flex items-center justify-center mx-auto mb-4 shadow-sm">
          <UserCog className="w-7 h-7" />
        </div>
        <h2 id="session-changed-title" className="text-xl font-bold text-slate-900 mb-2 tracking-tight">
          Your session changed in another tab
        </h2>
        <p id="session-changed-description" className="text-sm text-slate-600 leading-relaxed mb-6">
          {description}
        </p>
        <button
          ref={buttonRef}
          type="button"
          onClick={reload}
          className="w-full bg-[#033F99] hover:bg-[#022D6E] text-white font-bold py-3 rounded-xl shadow-lg shadow-[#033F99]/20 transition-all text-sm cursor-pointer"
        >
          Continue with the current session
        </button>
      </div>
    </div>
  );
}
